import React, { useEffect, useMemo, useRef, useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import { toast } from 'sonner'
import {
  MessageSquare, Search, Upload, Send, Phone, Tag,
  Plus, X as XIcon, ExternalLink, MoreVertical, Check, CheckCheck, Share2, ChevronLeft, Paperclip,
} from 'lucide-react'
import DashboardLayout from '../layouts/DashboardLayout'
import { useLanguage } from '../lib/LanguageContext'

// ── WhatsApp delivery ticks, exactly as the patient's own phone shows them (Paul 2026-07-17) ──
//   ✗ red    — it did not land. Something is wrong and someone must act.
//   ✓ grey   — sent: Twilio has it, the phone has not confirmed yet.
//   ✓✓ grey  — delivered to the patient's phone.
//   ✓✓ blue  — the patient read it.
//
// This used to be a hardcoded blue double-tick on EVERY clinic message — it told reception "they've
// read it" about messages that had failed outright. A tick that never changes is not information.
// No delivery record (messages sent before we tracked SIDs) => no tick, rather than a made-up one.
function DeliveryTick({ delivery }) {
  if (!delivery) return null
  const { tick, detail } = delivery
  const title = detail || tick

  if (tick === 'failed') {
    return (
      <span title={title} aria-label={`Not delivered: ${title}`} className="inline-flex items-center text-red-600">
        <XIcon size={13} strokeWidth={3} />
      </span>
    )
  }
  if (tick === 'read') {
    return (
      <span title={title} aria-label={`Read: ${title}`} className="inline-flex items-center text-sky-500">
        <CheckCheck size={13} />
      </span>
    )
  }
  if (tick === 'delivered') {
    return (
      <span title={title} aria-label={`Delivered: ${title}`} className="inline-flex items-center text-brand-muted">
        <CheckCheck size={13} />
      </span>
    )
  }
  return (
    <span title={title} aria-label={`Sent: ${title}`} className="inline-flex items-center text-brand-muted">
      <Check size={13} />
    </span>
  )
}

// WhatsApp Web-style 2-column conversations view:
//   left  → searchable list of threads (sidebar)
//   right → message bubbles + reply composer for the selected thread
//
// The Rails controller hydrates both the list AND the selected thread in
// one request — when you click a row we use Inertia partial reload with
// preserveScroll so only the right pane swaps.

export default function Conversations({
  conversations = [],
  selected_conversation = null,
  filters,
  all_tags = [],
  q = '',
  search_count = null,
  can_see_whatsapp_live: canSeeWhatsappLive = false,
}) {
  const { t, language } = useLanguage()
  // POPIA: importing the receptionist's WhatsApp history is an OWNER activity. Mirror the server gate
  // (ConversationsController before_action :require_admin!) — hide the upload UI unless owner. When
  // per-user auth is off (current_user absent) everyone on the shared password is treated as owner,
  // exactly as require_admin! does server-side.
  const { current_user } = usePage().props
  const isOwner = !current_user || current_user.role === 'admin'
  const [query, setQuery] = useState(q || '')
  const [importOpen, setImportOpen] = useState(false)
  const searchMounted = useRef(false)

  // Live WhatsApp-style refresh (Paul 2026-06-23; sped up + open-thread added 2026-07-06): poll every 5s
  // so a new/updated conversation jumps to the top AND new messages in the OPEN thread appear WITHOUT a
  // manual reload. Re-fetches BOTH the thread list (`conversations`) and the open thread
  // (`selected_conversation`) — the previous poll only refreshed the list, so messages arriving in the
  // conversation you were reading didn't show until you hit refresh. Composer text, search box and
  // scroll are preserved (preserveState/preserveScroll). Pauses while the browser tab is hidden.
  useEffect(() => {
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
      router.reload({ only: ['conversations', 'selected_conversation'], preserveScroll: true, preserveState: true })
    }, 5000)
    return () => clearInterval(id)
  }, [])

  // Server-side CONTENT search (WhatsApp-Web style): typing searches MESSAGE CONTENT across ALL
  // conversations on the server (debounced). The server returns matches — with the matching message
  // as the row preview — plus a count. e.g. search "directions" → every patient the system sent
  // directions to = a quick tally of bookings made. The server is authoritative when searching.
  useEffect(() => {
    if (!searchMounted.current) { searchMounted.current = true; return }
    const id = setTimeout(() => {
      router.get(
        '/conversations',
        { ...filters, q: query.trim() || undefined, selected_id: selected_conversation?.id || undefined },
        { preserveState: true, preserveScroll: true, only: ['conversations', 'search_count', 'q'], replace: true }
      )
    }, 350)
    return () => clearTimeout(id)
  }, [query])
  const filtered = conversations

  const handleFilter = (key, value) => {
    router.get(
      '/conversations',
      { ...filters, [key]: value || undefined, q: query.trim() || undefined, selected_id: selected_conversation?.id || undefined },
      { preserveState: true, preserveScroll: true }
    )
  }

  const openConversation = (c) => {
    router.get(
      '/conversations',
      { ...filters, q: query.trim() || undefined, selected_id: c.id },
      { preserveState: true, preserveScroll: true }
    )
  }

  // Mobile back: drop the selected_id so the list (sidebar) returns to full width.
  const closeConversation = () => {
    router.get(
      '/conversations',
      { ...filters, q: query.trim() || undefined },
      { preserveState: true, preserveScroll: true }
    )
  }

  return (
    <DashboardLayout>
      {/* On a phone this is a SINGLE pane that swaps list <-> thread; on md+ it's the
          WhatsApp-Web two-column view. 100dvh keeps it correct under the mobile address bar. */}
      <div className="flex h-[calc(100dvh-130px)] md:h-[calc(100vh-120px)] overflow-hidden rounded-xl border border-brand-accent/75 bg-white shadow-[0_24px_60px_-46px_rgba(57,60,77,0.35)]">

        {/* ── LEFT: sidebar (conversation list) — full width on mobile, hidden once a chat is open ── */}
        <aside className={`${selected_conversation ? 'hidden md:flex' : 'flex'} w-full flex-shrink-0 flex-col border-r border-brand-accent/60 bg-[#f0f2f5] md:w-[360px]`}>
          {/* Sidebar header */}
          <div className="flex items-center justify-between gap-2 bg-[#f0f2f5] px-4 py-3 border-b border-brand-accent/40">
            <h1 className="text-base font-semibold text-brand-ink">{t('conv_title')}</h1>
            {isOwner && (
              <button
                type="button"
                onClick={() => setImportOpen(true)}
                className="rounded-full p-2 text-brand-muted hover:bg-brand-accent/30 hover:text-brand-primary transition-colors"
                title="Import WhatsApp chat history"
                aria-label="Import WhatsApp chat history"
              >
                <Upload size={16} />
              </button>
            )}
          </div>

          {/* Search */}
          <div className="bg-white px-3 py-2 border-b border-brand-accent/30">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('conv_search')}
                className="w-full rounded-lg bg-[#f0f2f5] py-2 pl-9 pr-3 text-sm text-brand-ink placeholder:text-brand-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
              />
            </div>
          </div>

          {/* Filters row */}
          <div className="flex items-center gap-1.5 border-b border-brand-accent/30 bg-white px-3 py-2 overflow-x-auto">
            <MiniFilter
              value={filters?.channel || ''}
              onChange={(v) => handleFilter('channel', v)}
              options={[
                { value: '', label: t('conv_all_channels') },
                { value: 'whatsapp', label: 'WhatsApp AI' },
                // The 071 practice phone. Offered ONLY to an admin — reception works the AI line, and
                // mixing hundreds of reception chats into their queue buries what needs answering.
                ...(canSeeWhatsappLive ? [{ value: 'whatsapp_live', label: 'WhatsApp Live (071)' }] : []),
                { value: 'web_chat', label: 'Web chat' },
                { value: 'voice', label: 'Voice' },
              ]}
            />
            <MiniFilter
              value={filters?.tag || ''}
              onChange={(v) => handleFilter('tag', v)}
              options={[
                { value: '', label: 'All tags' },
                ...all_tags.map((tag) => ({ value: tag, label: tag })),
              ]}
            />
          </div>

          {/* Content-search result count — e.g. how many patients were sent "directions" (= bookings) */}
          {search_count !== null && (
            <div className="flex items-center justify-between bg-[#f0f2f5] px-4 py-1.5 text-xs font-medium text-brand-primary border-b border-brand-accent/30">
              <span>{search_count} {search_count === 1 ? 'conversation' : 'conversations'} match “{q}”</span>
              <button onClick={() => setQuery('')} className="text-brand-muted hover:text-brand-ink">clear</button>
            </div>
          )}
          {/* Conversation list */}
          <ul className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <li className="px-6 py-16 text-center text-sm text-brand-muted">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-brand-accent/30">
                  <MessageSquare size={18} className="text-brand-primary" />
                </div>
                {t('conv_no_match')}
              </li>
            ) : (
              filtered.map((c) => (
                <SidebarRow
                  key={c.id}
                  conv={c}
                  active={selected_conversation?.id === c.id}
                  onClick={() => openConversation(c)}
                  language={language}
                  t={t}
                />
              ))
            )}
          </ul>
        </aside>

        {/* ── RIGHT: chat thread — full width on mobile once open, hidden when showing the list ── */}
        <main className={`${selected_conversation ? 'flex' : 'hidden md:flex'} min-w-0 flex-1 flex-col bg-[#efeae2]`}>
          {selected_conversation ? (
            <ChatPane conv={selected_conversation} conversations={conversations} onBack={closeConversation} />
          ) : (
            <EmptyChatState t={t} />
          )}
        </main>
      </div>

      {isOwner && <ImportModal open={importOpen} onClose={() => setImportOpen(false)} t={t} />}
    </DashboardLayout>
  )
}

// ──────────────────────────────────────────────────────────────────────
// Sidebar row — WhatsApp-style conversation card
// ──────────────────────────────────────────────────────────────────────
function SidebarRow({ conv, active, onClick, language, t }) {
  const relative = formatShortTime(conv.updated_at, language)
  const preview = conv.last_message || conv.topic || `(${conv.message_count || 0} ${t('conv_msgs') || 'messages'})`
  const isFlagged = (conv.tags || []).includes('needs_review')
  const isImport = conv.source === 'import' || (conv.tags || []).includes('human-receptionist')

  // Attention colour (Paul 2026-08-27): the patient spoke LAST and we haven't answered.
  //   red    = waiting AND no one has opened it yet (missed — this patient needs answering)
  //   yellow = waiting but a staff member has opened it (seen, being handled)
  //   (null) = we replied last, i.e. assisted/addressed → normal white.
  // The SELECTED row is being read right now, so it takes the neutral selected tint, not red/yellow.
  // INLINE colours on purpose: Tailwind's JIT purged `bg-yellow-200` (it only scans literal class strings
  // and this one appeared nowhere else), so a class-based tint silently rendered no colour. Inline styles
  // can't be purged and pin the exact shade. A left bar makes the state readable at a glance.
  const attnBg = active ? null : conv.attention === 'red' ? '#fecaca' : conv.attention === 'yellow' ? '#fde047' : null
  const attnBar = conv.attention === 'red' ? '#dc2626' : conv.attention === 'yellow' ? '#ca8a04' : 'transparent'
  const rowClass = active ? 'bg-[#e7f3ec]' : attnBg ? '' : 'hover:bg-[#f5f6f6]'

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        style={{ ...(attnBg ? { backgroundColor: attnBg } : {}), borderLeft: `4px solid ${attnBar}` }}
        className={`w-full text-left flex items-center gap-3 px-3 py-2.5 border-b border-brand-accent/20 transition-colors ${rowClass}`}
      >
        <div className="flex-shrink-0 w-11 h-11 rounded-full bg-brand-primary/15 flex items-center justify-center text-brand-primary text-sm font-semibold">
          {initials(conv.patient_name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="truncate text-sm font-medium text-brand-ink">
              {conv.patient_name || conv.patient_phone}
            </p>
            <span className="flex-shrink-0 text-[11px] text-brand-muted">{relative}</span>
          </div>
          <div className="flex items-center justify-between gap-2 mt-0.5">
            <p className="truncate text-xs text-brand-muted">{preview}</p>
            <div className="flex items-center gap-1 flex-shrink-0">
              {isImport && (
                <span
                  className="inline-flex items-center rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700"
                  title="Imported receptionist history — read only"
                >
                  Imported
                </span>
              )}
              {isFlagged && (
                <span
                  className="inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700"
                  title="Needs reception review"
                >
                  Flag
                </span>
              )}
              {conv.language === 'af' && (
                <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                  AF
                </span>
              )}
            </div>
          </div>
        </div>
      </button>
    </li>
  )
}

// ──────────────────────────────────────────────────────────────────────
// Chat pane (right side) — header, scrolling bubble feed, composer
// ──────────────────────────────────────────────────────────────────────
// WhatsApp-style quick emoji set for the composer picker (dental/reception-relevant ones included).
const EMOJIS = ['😀','😃','😄','😁','😆','😅','😂','🤣','😊','😇','🙂','🙃','😉','😌','😍','🥰','😘','😋','😎','🤩','🥳','🤔','🤨','😐','😏','😒','🙄','😬','😮','😯','😴','😭','😤','😠','👍','👎','👌','🙏','👏','🙌','👋','🤝','💪','🦷','❤️','🧡','💛','💚','💙','💜','🤍','✅','❌','⭐','🔥','🎉','💯','📅','📞','💬','⏰']

function ChatPane({ conv, conversations = [], onBack }) {
  const [body, setBody] = useState('')
  const [sending, setSending] = useState(false)
  const [showEmoji, setShowEmoji] = useState(false)
  const [fwd, setFwd] = useState(null)
  const scrollRef = useRef(null)
  const textareaRef = useRef(null)

  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [conv.id, conv.messages?.length])

  // An imported thread is the human receptionist's HISTORICAL export — read only. No composer, so
  // no message can be sent into a historical record (mirrors the server-side read-only reply guard).
  const isImport = conv.source === 'import'
  const canReply = conv.channel === 'whatsapp' && !isImport

  // Correlate each patient-sent file (InboundMedia) to the message it arrived with, by nearest
  // timestamp, each file used once — so attachments render inline where the patient sent them. Anything
  // we can't place by time is pinned to the last message so it's still openable. (Paul 2026-08-04)
  const mediaByIdx = useMemo(() => {
    const map = {}
    const pool = [...(conv.inbound_media || [])].sort((a, b) => new Date(a.received_at) - new Date(b.received_at))
    if (!pool.length) return map
    const used = new Set()
    ;(conv.messages || []).forEach((m, i) => {
      if (m.role === 'assistant' || !m.timestamp) return
      const mt = new Date(m.timestamp).getTime()
      const hit = pool.filter((im) => !used.has(im.id) && Math.abs(new Date(im.received_at).getTime() - mt) < 120000)
      if (hit.length) { map[i] = hit; hit.forEach((im) => used.add(im.id)) }
    })
    const leftover = pool.filter((im) => !used.has(im.id))
    if (leftover.length) {
      let last = (conv.messages || []).length - 1
      map[last] = [...(map[last] || []), ...leftover]
    }
    return map
  }, [conv.messages, conv.inbound_media])

  // RENDER BY TIME (Paul 2026-09-10): the patient's row carries its arrival time while the turn's automatic
  // sends (confirmation / directions / intake) were appended earlier with their send time — array order put
  // our replies above the message they answered. Stable sort by timestamp; the original index is kept so
  // inline attachments (mediaByIdx) still land on the right bubble.
  const orderedMessages = useMemo(() => {
    const ts = (m) => (m.timestamp ? new Date(m.timestamp).getTime() : 0)
    return (conv.messages || []).map((m, i) => ({ m, i })).sort((a, b) => (ts(a.m) - ts(b.m)) || (a.i - b.i))
  }, [conv.messages])

  const handleSend = (e) => {
    e?.preventDefault?.()
    const trimmed = body.trim()
    if (!trimmed) return
    // A SECOND SUBMIT WHILE THE FIRST IS STILL IN FLIGHT MUST DO NOTHING (Paul 2026-07-20). The
    // button and textarea are disabled on `sending`, but that is not the whole story: Enter is
    // handled on keyDown, a mobile tap can fire twice, and React applies `sending` on the NEXT
    // render - so a fast second event still reached router.post and the patient got two messages.
    // Reading the flag here closes it at the source. The server-side guard in
    // ConversationsController#reply is the real guarantee (this cannot protect a second tab).
    if (sending) return
    setSending(true)
    router.post(`/conversations/${conv.id}/reply`, { body: trimmed }, {
      preserveScroll: true,
      onSuccess: () => { setBody(''); toast.success('Reply sent — AI paused 4 hrs on this conversation') },
      onError:   () => toast.error('Could not send reply'),
      onFinish:  () => setSending(false),
    })
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend(e)
    }
  }

  // Insert an emoji at the textarea cursor (WhatsApp-style), keeping focus + caret position.
  const insertEmoji = (emoji) => {
    const el = textareaRef.current
    const start = el ? el.selectionStart : body.length
    const end = el ? el.selectionEnd : body.length
    setBody(body.slice(0, start) + emoji + body.slice(end))
    setShowEmoji(false)
    requestAnimationFrame(() => {
      if (el) { el.focus(); const pos = start + emoji.length; el.setSelectionRange(pos, pos) }
    })
  }

  // AI-drafted clinical suggestion (Paul 2026-07-29): "Use it" loads the draft into the composer so
  // reception can EDIT it before sending (never auto-sent); "Dismiss" clears it without sending.
  const useSuggestion = () => {
    setBody(conv.suggested_reply || '')
    requestAnimationFrame(() => textareaRef.current?.focus())
  }
  const dismissSuggestion = () => {
    router.patch(`/conversations/${conv.id}/clear_suggested_reply`, {}, {
      preserveScroll: true,
      onSuccess: () => toast.success('Suggestion dismissed'),
      onError: () => toast.error('Could not dismiss the suggestion'),
    })
  }

  return (
    <>
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-brand-accent/30 bg-[#f0f2f5] px-3 py-3 md:px-4">
        <button
          type="button"
          onClick={onBack}
          className="md:hidden -ml-1 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-brand-muted transition-colors hover:bg-black/5 hover:text-brand-ink"
          aria-label="Back to conversations"
        >
          <ChevronLeft size={22} />
        </button>
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand-primary/15 flex items-center justify-center text-brand-primary text-sm font-semibold">
          {initials(conv.patient_name)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-brand-ink">
            {conv.patient_name || conv.patient_phone}
          </p>
          <p className="truncate text-[11px] text-brand-muted">
            <Phone size={10} className="inline mr-1" />
            {conv.patient_phone}
            {conv.language && (
              <span className="ml-2 text-brand-primary/70">{conv.language === 'af' ? 'Afrikaans' : 'English'}</span>
            )}
          </p>
        </div>
        {conv.patient_id && (
          <a
            href={`/patients/${conv.patient_id}`}
            className="inline-flex items-center gap-1 rounded-full border border-brand-primary/20 bg-white/60 px-2.5 py-1 text-[11px] font-semibold text-brand-primary hover:bg-brand-primary/10 transition-colors"
          >
            <ExternalLink size={10} /> Patient
          </a>
        )}
      </div>

      {/* Bubble feed */}
      <div
        ref={scrollRef}
        className="min-w-0 flex-1 overflow-y-auto px-4 py-5 space-y-2 bg-[#efeae2]"
        style={{ backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%22100%22><rect width=%22100%22 height=%22100%22 fill=%22%23efeae2%22/></svg>')" }}
      >
        {conv.messages?.length > 0 ? (
          orderedMessages.map(({ m, i }, pos) => {
            const prev = orderedMessages[pos - 1]?.m
            const showDivider = m.timestamp && dayKey(m.timestamp) !== dayKey(prev?.timestamp)
            return (
              <React.Fragment key={i}>
                {showDivider && <DayDivider label={dayLabel(m.timestamp)} />}
                <Bubble msg={m} media={mediaByIdx[i]} onForward={setFwd} />
              </React.Fragment>
            )
          })
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-brand-muted">
            No messages yet
          </div>
        )}
      </div>

      {/* AI-drafted SUGGESTED reply for a clinical estimate question (Paul 2026-07-29). Shown to reception
          to VET WITH THE DENTIST — editable, never auto-sent. "Use it" loads it into the composer. */}
      {canReply && conv.suggested_reply && (
        <div className="border-t border-amber-200 bg-amber-50 px-3 py-2.5">
          <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-amber-800">
            🤖 AI-drafted suggested reply — show Dr Chalita before sending
          </div>
          <div className="whitespace-pre-wrap rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm text-brand-ink">
            {conv.suggested_reply}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <button type="button" onClick={useSuggestion}
              className="rounded-lg bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-primary/90">
              Use &amp; edit reply
            </button>
            <button type="button" onClick={dismissSuggestion}
              className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100">
              Dismiss
            </button>
            <span className="text-[11px] text-amber-700">Loads into the box below — edit, then send.</span>
          </div>
        </div>
      )}

      {/* Composer */}
      {canReply ? (
        <form
          onSubmit={handleSend}
          className="relative flex items-end gap-2 border-t border-brand-accent/30 bg-[#f0f2f5] px-3 py-2"
        >
          <button
            type="button"
            onClick={() => toast.info('Attachments are coming soon')}
            title="Attach a file"
            aria-label="Attach"
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-brand-muted transition-colors hover:bg-black/5 hover:text-brand-ink"
          >
            <Plus size={20} />
          </button>
          <div className="relative flex-shrink-0">
            {showEmoji && (
              <div className="absolute bottom-12 left-0 z-20 grid max-h-56 w-72 grid-cols-8 gap-1 overflow-y-auto rounded-lg border border-brand-accent/30 bg-white p-2 shadow-lg">
                {EMOJIS.map((em, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => insertEmoji(em)}
                    className="rounded p-1 text-xl leading-none hover:bg-brand-primary/10"
                  >
                    {em}
                  </button>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => setShowEmoji((s) => !s)}
              className="flex h-10 w-10 items-center justify-center rounded-full text-2xl transition-colors hover:bg-black/5"
              aria-label="Emoji"
            >
              😀
            </button>
          </div>
          <textarea
            ref={textareaRef}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={sending}
            placeholder="Type a message"
            rows={1}
            className="flex-1 resize-none max-h-28 min-h-[42px] rounded-lg border-0 bg-white px-3 py-2.5 text-sm text-brand-ink placeholder:text-brand-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
          />
          <button
            type="submit"
            disabled={sending || !body.trim()}
            className="h-10 w-10 rounded-full bg-brand-primary text-white flex items-center justify-center hover:bg-brand-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
            aria-label="Send"
          >
            {sending ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            ) : (
              <Send size={16} />
            )}
          </button>
        </form>
      ) : isImport ? (
        <div className="border-t border-brand-accent/30 bg-indigo-50 px-4 py-3 text-xs text-indigo-700">
          Imported receptionist history — read only. The AI never replies to an imported chat.
        </div>
      ) : (
        <div className="border-t border-brand-accent/30 bg-amber-50 px-4 py-3 text-xs text-amber-700">
          Replies are only supported on WhatsApp conversations.
        </div>
      )}

      {fwd != null && (
        <ForwardModal message={fwd} fromId={conv.id} conversations={conversations} onClose={() => setFwd(null)} />
      )}
    </>
  )
}

// ──────────────────────────────────────────────────────────────────────
// Bubble — message in chat feed
// ──────────────────────────────────────────────────────────────────────
// Render WhatsApp-style markdown (*bold* _italic_ ~strike~ `mono`) as React nodes.
// Safe by construction — text is kept in {expressions} so React escapes it; no HTML injection.
function renderWhatsappText(text) {
  if (text == null || text === '') return text
  const re = /(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~|`[^`\n]+`)/g
  return String(text).split(re).map((part, i) => {
    if (/^\*[^*\n]+\*$/.test(part)) return <strong key={i} className="font-semibold">{part.slice(1, -1)}</strong>
    if (/^_[^_\n]+_$/.test(part)) return <em key={i}>{part.slice(1, -1)}</em>
    if (/^~[^~\n]+~$/.test(part)) return <span key={i} className="line-through">{part.slice(1, -1)}</span>
    if (/^`[^`\n]+`$/.test(part)) return <code key={i} className="rounded bg-black/5 px-1 font-mono text-[12px]">{part.slice(1, -1)}</code>
    return part
  })
}

// First http(s) URL in a message (trailing punctuation trimmed).
const URL_RE = /(https?:\/\/[^\s]+)/i
function firstUrl(text) {
  const m = String(text || '').match(URL_RE)
  return m ? m[1].replace(/[)\].,!?]+$/, '') : null
}

// Session cache so each URL's preview is fetched at most once.
const linkPreviewCache = new Map()

// WhatsApp-style link preview card (Open-Graph), fetched via the SSRF-guarded /link_preview endpoint.
function LinkPreviewCard({ url }) {
  const [data, setData] = useState(() => linkPreviewCache.get(url) || null)
  useEffect(() => {
    if (!url || linkPreviewCache.has(url)) return
    let alive = true
    fetch(`/link_preview?url=${encodeURIComponent(url)}`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : {}))
      .then((j) => { linkPreviewCache.set(url, j); if (alive) setData(j) })
      .catch(() => { linkPreviewCache.set(url, {}); if (alive) setData({}) })
    return () => { alive = false }
  }, [url])
  if (!data || (!data.title && !data.image)) return null
  return (
    <a href={url} target="_blank" rel="noopener noreferrer"
       className="mt-1.5 block overflow-hidden rounded-lg border border-black/10 bg-white/80 transition-colors hover:bg-white">
      {data.image && (
        <img src={data.image} alt="" loading="lazy" className="h-28 w-full object-cover"
             onError={(e) => { e.currentTarget.style.display = 'none' }} />
      )}
      <div className="px-2.5 py-1.5">
        {data.title && <p className="line-clamp-2 text-xs font-semibold text-brand-ink">{data.title}</p>}
        {data.description && <p className="mt-0.5 line-clamp-2 text-[11px] text-brand-muted">{data.description}</p>}
        {data.host && <p className="mt-0.5 text-[10px] text-brand-muted">{data.host}</p>}
      </div>
    </a>
  )
}

// Forward a message to another WhatsApp conversation (pick a target chat → POST /forward).
function ForwardModal({ message, fromId, conversations, onClose }) {
  const [q, setQ] = useState('')
  const targets = (conversations || []).filter((c) => c.id !== fromId && c.channel === 'whatsapp' && c.patient_phone)
  const filtered = q.trim()
    ? targets.filter((c) => `${c.patient_name || ''} ${c.patient_phone || ''}`.toLowerCase().includes(q.toLowerCase()))
    : targets
  const forwardTo = (target) => {
    router.post(`/conversations/${fromId}/forward`, { message, to_conversation_id: target.id }, {
      preserveScroll: true,
      onSuccess: () => { toast.success(`Forwarded to ${target.patient_name || target.patient_phone}`); onClose?.() },
      onError:   () => toast.error('Could not forward'),
    })
  }
  return (
    <Modal open onClose={onClose} title="Forward message">
      <p className="mb-3 line-clamp-3 whitespace-pre-wrap rounded-lg bg-brand-surface/50 px-3 py-2 text-xs text-brand-muted">{message}</p>
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search a chat to forward to…"
        className="mb-2 w-full rounded-lg border border-brand-border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
      />
      <div className="max-h-72 overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="px-2 py-4 text-center text-sm text-brand-muted">No other WhatsApp chats to forward to.</p>
        ) : (
          filtered.map((c) => (
            <button
              key={c.id}
              onClick={() => forwardTo(c)}
              className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm transition-colors hover:bg-brand-surface/60"
            >
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-brand-primary/15 text-xs font-semibold text-brand-primary">
                {initials(c.patient_name)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-brand-ink">{c.patient_name || c.patient_phone}</span>
                <span className="block truncate text-[11px] text-brand-muted">{c.patient_phone}</span>
              </span>
            </button>
          ))
        )}
      </div>
    </Modal>
  )
}

// WhatsApp-style centred day divider so a message from another day can never read as if it
// happened just now (Paul 2026-07-02 — "confirmed 14:00" from yesterday looked like today).
function DayDivider({ label }) {
  if (!label) return null
  return (
    <div className="flex justify-center py-1.5">
      <span className="rounded-md bg-white/85 px-3 py-1 text-[11px] font-medium text-brand-muted shadow-sm">
        {label}
      </span>
    </div>
  )
}

// A patient-sent WhatsApp file (InboundMedia). Images render as a tappable thumbnail; everything else
// (PDF, Word/Excel, voice note, video, contact card) as an "Open" chip. The /media/:id URL serves the
// file inline (append ?dl=1 to force a download). (Paul 2026-08-04)
function MediaAttachment({ file }) {
  const isImage = (file.content_type || '').startsWith('image/')
  const name = file.filename || 'attachment'
  if (isImage) {
    return (
      <a href={file.url} target="_blank" rel="noopener noreferrer" className="block overflow-hidden rounded-lg border border-black/10">
        <img src={file.url} alt={name} loading="lazy" className="max-h-64 w-auto max-w-full object-contain" />
      </a>
    )
  }
  return (
    <a href={file.url} target="_blank" rel="noopener noreferrer"
       className="flex items-center gap-2 rounded-lg border border-black/10 bg-white/80 px-2.5 py-2 text-sm transition-colors hover:bg-white">
      <Paperclip size={16} className="flex-shrink-0 text-brand-primary" />
      <span className="min-w-0 flex-1 truncate text-brand-ink">{name}</span>
      <span className="flex-shrink-0 text-[11px] font-semibold text-brand-primary">Open</span>
    </a>
  )
}

function Bubble({ msg, media, onForward }) {
  const isClinic = msg.role === 'assistant'
  const hasMedia = Array.isArray(media) && media.length > 0
  // A media message with no caption arrives as a bare "[attachment]" — hide that dead label once we have
  // the real file to show; keep any real caption text.
  const isBareAttachment = (msg.content || '').trim() === '[attachment]'
  const showText = !!msg.content && !(hasMedia && isBareAttachment)
  const previewUrl = showText ? firstUrl(msg.content) : null
  const fwdBtn = onForward && msg.content ? (
    <button
      type="button"
      onClick={() => onForward(msg.content)}
      title="Forward to another chat"
      className="flex-shrink-0 text-brand-muted opacity-0 transition-opacity hover:text-brand-primary group-hover:opacity-100"
    >
      <Share2 size={14} />
    </button>
  ) : null
  return (
    <div className={`group flex items-center gap-1.5 ${isClinic ? 'justify-end' : 'justify-start'}`}>
      {isClinic && fwdBtn}
      <div
        className={`max-w-[85%] md:max-w-[70%] rounded-lg px-3 py-2 shadow-sm ${
          isClinic
            ? 'bg-[#d9fdd3] text-brand-ink rounded-tr-sm'
            : 'bg-white text-brand-ink rounded-tl-sm'
        }`}
      >
        {hasMedia && (
          <div className={`space-y-1.5 ${showText ? 'mb-1.5' : ''}`}>
            {media.map((f) => <MediaAttachment key={f.id} file={f} />)}
          </div>
        )}
        {showText && <p className="text-[15px] leading-relaxed whitespace-pre-wrap break-words">{renderWhatsappText(msg.content)}</p>}
        {previewUrl && <LinkPreviewCard url={previewUrl} />}
        {(msg.timestamp || msg.via) && (
          <p className="text-[10px] mt-0.5 text-brand-muted text-right flex items-center justify-end gap-1">
            {/* Where the message actually travelled. A row with `via` did not go through Ivory: it was
                sent or received on the practice PHONE's own WhatsApp (071) and mirrored in, so reception
                can see the whole relationship in one thread. Without the badge a staff member would
                read reception's own message as something the AI said. */}
            {msg.via && (
              <span
                title={msg.via === '071' ? 'Sent or received on the practice phone (071) WhatsApp — mirrored into this thread, not sent by Ivory' : `via ${msg.via}`}
                className="rounded-full bg-brand-accent/25 px-1.5 py-px font-semibold text-brand-ink/70"
              >
                {msg.via === '071' ? '071 phone' : msg.via}
              </span>
            )}
            {msg.timestamp && formatTime(msg.timestamp)}
            {isClinic && !msg.via && <DeliveryTick delivery={msg.delivery} />}
          </p>
        )}
      </div>
      {!isClinic && fwdBtn}
    </div>
  )
}

function EmptyChatState({ t }) {
  return (
    <div className="flex h-full flex-col items-center justify-center text-center px-8">
      <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-white shadow">
        <MessageSquare size={32} className="text-brand-primary" />
      </div>
      <h2 className="text-lg font-semibold text-brand-ink">Select a conversation</h2>
      <p className="mt-1 max-w-xs text-sm text-brand-muted">
        Pick a chat on the left to read messages and reply on behalf of the practice.
      </p>
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────
// Mini filter pill (used in sidebar)
// ──────────────────────────────────────────────────────────────────────
function MiniFilter({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-full bg-[#f0f2f5] border border-brand-accent/40 px-2.5 py-1 text-[11px] font-medium text-brand-ink hover:bg-brand-accent/20 focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  )
}

// ──────────────────────────────────────────────────────────────────────
// Import modal (kept compatible with original Conversations page)
// ──────────────────────────────────────────────────────────────────────
function ImportModal({ open, onClose, t }) {
  const [file, setFile] = useState(null)
  const [ownerName, setOwnerName] = useState('Dr Le Roux')
  const [patientPhone, setPatientPhone] = useState('')
  const [submitting, setSubmitting] = useState(false)

  if (!open) return null

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!file) return
    setSubmitting(true)
    const fd = new FormData()
    fd.append('file', file)
    if (ownerName.trim()) fd.append('owner_name', ownerName.trim())
    if (patientPhone.trim()) fd.append('patient_phone', patientPhone.trim())
    router.post('/conversations/import', fd, {
      forceFormData: true,
      onFinish: () => { setSubmitting(false); onClose() },
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4">
      <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-brand-ink">Import receptionist WhatsApp history</h2>
          <button onClick={onClose} className="text-brand-muted hover:text-brand-ink"><XIcon size={18} /></button>
        </div>
        <p className="mb-4 text-xs text-brand-muted leading-relaxed">
          In WhatsApp, open the receptionist's chat with a patient → <b>⋮ / contact name → Export chat → Without media</b> →
          save the <b>.txt</b>. Upload it here. It imports read-only, tagged <b>human-receptionist</b>, so you can review it in
          this list. To bring in many chats at once, zip the exported <b>.txt</b> files and upload the <b>.zip</b>.
        </p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-brand-muted mb-1">Exported chat file (.txt, .zip, or .json)</label>
            <input type="file" accept=".json,.txt,.zip" onChange={(e) => setFile(e.target.files[0])}
              className="w-full text-xs file:mr-2 file:rounded file:border-0 file:bg-brand-primary file:text-white file:px-3 file:py-1.5 file:text-xs file:font-medium" />
          </div>
          <div>
            <label className="block text-xs font-medium text-brand-muted mb-1">Receptionist / practice name as it appears in the chat</label>
            <input type="text" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} placeholder="e.g. Dr Le Roux"
              className="w-full text-sm rounded-lg border border-brand-accent/60 px-3 py-2" />
            <p className="mt-1 text-[11px] text-brand-muted">Their messages become the practice side (outbound). Leave blank to auto-detect.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-brand-muted mb-1">Patient WhatsApp number (for .txt uploads)</label>
            <input type="text" value={patientPhone} onChange={(e) => setPatientPhone(e.target.value)} placeholder="+27..."
              className="w-full text-sm rounded-lg border border-brand-accent/60 px-3 py-2" />
            <p className="mt-1 text-[11px] text-brand-muted">A .txt export uses names, not numbers — this links the chat to the patient's record.</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-3 py-2 text-sm text-brand-muted hover:text-brand-ink">Cancel</button>
            <button type="submit" disabled={!file || submitting} className="px-3 py-2 text-sm rounded-lg bg-brand-primary text-white hover:bg-brand-primary/90 disabled:opacity-40">
              {submitting ? 'Importing…' : 'Import'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────
function initials(name = '') {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('') || '·'
}

function formatTime(iso) {
  try {
    const d = new Date(iso)
    return d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

// A stable YYYY-MM-DD day key for grouping messages by calendar day (local time).
function dayKey(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d)) return ''
  return d.toDateString()
}

// WhatsApp-style day divider label: "Today" / "Yesterday" / "Wed, 1 Jul 2026".
// This is the fix for the confusion where the thread only showed the time-of-day,
// making an exchange from a different day read as if it happened now (Paul 2026-07-02).
function dayLabel(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d)) return ''
  const now = new Date()
  if (d.toDateString() === now.toDateString()) return 'Today'
  const y = new Date(now)
  y.setDate(now.getDate() - 1)
  if (d.toDateString() === y.toDateString()) return 'Yesterday'
  return d.toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
}

function formatShortTime(iso, language) {
  if (!iso) return ''
  const locale = language === 'af' ? 'af-ZA' : 'en-ZA'
  const d = new Date(iso)
  const now = new Date()
  const sameDay = d.toDateString() === now.toDateString()
  if (sameDay) {
    return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
  }
  const diffDays = Math.round((now - d) / (1000 * 60 * 60 * 24))
  if (diffDays < 7) {
    return d.toLocaleDateString(locale, { weekday: 'short' })
  }
  return d.toLocaleDateString(locale, { month: 'short', day: 'numeric' })
}
